// src/components/texting/GroupTextModal.jsx
// "Group text": pick up to 20 leads, write one message, and EACH person gets
// their own private text with {firstName} filled in per person. This is not
// a group chat (business SMS can't do those): recipients never see each
// other, and every text lands on its own lead thread. Capped so it stays
// personal outreach, never blast marketing.
import React, { useMemo, useState } from "react";
import { X, Users, Send } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { clientNames } from "../../utils/leadClients";
import { useActionDialog } from "../ActionDialog";
import {
  sendText,
  contactPhoneOf,
  renderTemplate,
  firstNameOf,
  normalizePhoneE164,
  useFromNumberOptions,
  savePreferredFrom,
  canUseTexting,
} from "../../utils/texting";

// One batch tops out at 100 people and sends are paced ~2/second. Carriers
// watch for blast behavior; identical texts to huge lists get numbers
// filtered. Keep batches modest and personal.
const MAX_RECIPIENTS = 100;
const SEND_PACE_MS = 450;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function GroupTextModal({ leads, onClose }) {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [selected, setSelected] = useState({});
  const [message, setMessage] = useState(
    "Hi {firstName}! Just a quick note from Team Edelstein.",
  );
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState("");
  const { options: fromOptions, defaultNumber } = useFromNumberOptions(user);
  const [fromNumber, setFromNumber] = useState("");
  const effectiveFrom = fromNumber || defaultNumber;

  const eligible = useMemo(
    () =>
      (leads || [])
        .filter((l) => l.status === "active" && l.contactId && !l.doNotText)
        .map((l) => ({
          id: l.id,
          contactId: l.contactId,
          name: clientNames(l) || l.contactName || "(unnamed)",
          contactName: l.contactName || "",
          stage: l.stage || "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [leads],
  );
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? eligible.filter((l) => l.name.toLowerCase().includes(q)) : eligible;
  }, [eligible, search]);

  const picked = eligible.filter((l) => selected[l.id]);

  // Mass mode: pasted raw numbers, each its own private conversation.
  const [pasted, setPasted] = useState("");
  const pastedNumbers = useMemo(() => {
    const seen = new Set();
    const valid = [];
    let invalid = 0;
    pasted
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((raw) => {
        const num = normalizePhoneE164(raw);
        if (!num) {
          invalid += 1;
          return;
        }
        if (!seen.has(num)) {
          seen.add(num);
          valid.push(num);
        }
      });
    return { valid, invalid };
  }, [pasted]);

  const totalRecipients = picked.length + pastedNumbers.valid.length;

  const toggle = (id) => {
    setSelected((s) => {
      if (s[id]) {
        const next = { ...s };
        delete next[id];
        return next;
      }
      if (Object.keys(s).length >= MAX_RECIPIENTS) {
        toast.info(`Keep it personal: ${MAX_RECIPIENTS} people max per group text.`);
        return s;
      }
      return { ...s, [id]: true };
    });
  };

  const sendAll = async () => {
    if (!canUseTexting(user) || sending) return;
    const body = message.trim();
    if (!body || totalRecipients === 0) return;
    if (totalRecipients > MAX_RECIPIENTS) {
      toast.error(`That's ${totalRecipients} people. ${MAX_RECIPIENTS} max per batch.`);
      return;
    }
    const sure = await confirm({
      title: "Send Group Text",
      body: `Text ${totalRecipients} ${totalRecipients === 1 ? "person" : "people"}?`,
      note: "Each gets their own private message.",
      confirmLabel: "Send",
    });
    if (!sure) return;
    setSending(true);
    const agentName =
      [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "";
    let done = 0;
    let ok = 0;
    let noPhone = 0;
    let failed = 0;
    const sendOne = async ({ to, firstName, leadId, contactName }) => {
      done += 1;
      setProgress(`Sending ${done} of ${totalRecipients}…`);
      try {
        const res = await sendText({
          to,
          body: renderTemplate(body, { firstName, agentName }),
          fromNumber: effectiveFrom,
          leadId: leadId || "",
          contactName: contactName || "",
          user,
          source: "manual",
        });
        if (res.ok) ok += 1;
        else failed += 1;
      } catch (e) {
        console.error("group text send failed:", e);
        failed += 1;
      }
      await sleep(SEND_PACE_MS);
    };
    for (const lead of picked) {
      const phone = await contactPhoneOf(lead.contactId);
      if (!phone) {
        done += 1;
        noPhone += 1;
        continue;
      }
      await sendOne({
        to: phone,
        firstName: firstNameOf(lead.contactName || lead.name) || "there",
        leadId: lead.id,
        contactName: lead.contactName || lead.name,
      });
    }
    for (const num of pastedNumbers.valid) {
      await sendOne({ to: num, firstName: "there" });
    }
    setSending(false);
    setProgress("");
    const parts = [`Sent ${ok}`];
    if (noPhone) parts.push(`${noPhone} had no number`);
    if (failed) parts.push(`${failed} failed`);
    toast[failed ? "error" : "success"](parts.join(" · "));
    if (ok > 0) onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-lg max-h-[88vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between">
            <div className="text-base font-bold flex items-center gap-2">
              <Users size={16} /> Group text
            </div>
            <button onClick={onClose} className="p-1 text-slate-300 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto">
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Each person gets their own private text. Nobody sees anyone else,
              and every message is logged on that lead's thread.
            </p>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              From:
              <select
                value={effectiveFrom}
                onChange={(e) => {
                  setFromNumber(e.target.value);
                  savePreferredFrom(user?.id, e.target.value);
                }}
                className="text-xs font-semibold text-linkColor bg-slate-50 border border-slate-300 rounded-md px-2 py-1"
              >
                {fromOptions.map((o) => (
                  <option key={o.num} value={o.num}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:border-themeColor outline-none"
            />
            <p className="text-[11px] text-slate-400 -mt-1">
              {"{firstName}"} fills in each person's name automatically.
            </p>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads…"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:border-themeColor"
            />
            <div className="border border-slate-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-slate-50">
              {shown.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  No textable leads match.
                </p>
              ) : (
                shown.map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[l.id]}
                      onChange={() => toggle(l.id)}
                    />
                    <span className="font-medium text-slate-800">{l.name}</span>
                    <span className="ml-auto text-[11px] text-slate-400">
                      {l.stage}
                    </span>
                  </label>
                ))
              )}
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Or paste phone numbers (mass text)
              </label>
              <textarea
                rows={3}
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder={"One per line or comma-separated:\n(651) 555-0142\n612-555-0188"}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:border-themeColor outline-none font-mono"
              />
              <p className="text-[11px] text-slate-400 mt-0.5">
                {pastedNumbers.valid.length > 0 &&
                  `${pastedNumbers.valid.length} number${pastedNumbers.valid.length === 1 ? "" : "s"} ready. `}
                {pastedNumbers.invalid > 0 &&
                  `${pastedNumbers.invalid} not recognized. `}
                Each number becomes its own conversation in the Inbox. Anyone
                who ever replied STOP is skipped automatically.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {totalRecipients} of {MAX_RECIPIENTS} max ({picked.length} leads
              + {pastedNumbers.valid.length} numbers)
            </span>
            <button
              onClick={sendAll}
              disabled={sending || totalRecipients === 0 || !message.trim()}
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#1E293B] rounded-lg px-4 py-2 disabled:opacity-50"
            >
              <Send size={13} />
              {sending
                ? progress || "Sending…"
                : `Send to ${totalRecipients || "…"}`}
            </button>
          </div>
        </div>
      </div>
      {dialog}
    </>
  );
}
